/**
 * settings-row.tsx — Danh sách cài đặt kiểu "mỗi cài đặt một dòng" (theo mẫu HelpKnow) cho màn Dạy nhân viên AI.
 *
 *   Trái : tên cài đặt + ⓘ (tooltip giải thích ngắn) + nhãn nhỏ "Beta" / "Sắp có" (tuỳ chọn).
 *   Phải : tuỳ loại
 *     - `dialog`  → chữ "Cài đặt" (hoặc "Cài đặt (n)") màu xám + mũi tên › — cả dòng bấm được, mở hộp thoại chi tiết.
 *     - `status`  → badge "Đã bật" (xanh) / "Chưa bật" (xám) + mũi tên › — bấm mở hộp thoại có công tắc ở đầu.
 *     - `switch`  → công tắc ngay trên dòng.
 *   Dòng cao ~56px, chữ 15px, phân cách bằng đường kẻ mảnh; rê chuột đổi nền nhẹ.
 *   `SettingsList` bọc danh sách; có `collapsedCount`/"Hiển thị thêm cài đặt ⌄" để ẩn các dòng nâng cao.
 */
import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, ChevronUp, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Switch, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { cn } from '@/lib/utils'

export function InfoTip({ text }: { text: ReactNode }) {
  const { t } = useTranslation('aiTrainUi')
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help text-muted-foreground/70 hover:text-muted-foreground" aria-label={t('settingsRow.explain')}>
          <Info className="h-4 w-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  )
}

export function StatusBadge({ on, labels }: { on: boolean; labels?: [string, string] }) {
  const { t } = useTranslation('aiTrainUi')
  const [onLabel, offLabel] = labels ?? [t('settingsRow.on'), t('settingsRow.off')]
  return (
    <Badge variant={on ? 'success' : 'secondary'} className={cn('px-2 py-0.5 text-[12px] font-medium', !on && 'text-muted-foreground')}>
      {on ? onLabel : offLabel}
    </Badge>
  )
}

export type SettingsRowProps = {
  icon?: ReactNode
  title: ReactNode
  /** Giải thích ngắn hiện trong tooltip ⓘ. */
  info?: ReactNode
  /** Nhãn nhỏ cạnh tên: "Beta" (cam nhạt) / "Sắp có". */
  tag?: 'beta' | 'soon' | null
  disabled?: boolean
} & (
  | { kind: 'dialog'; onOpen: () => void; count?: number | null; label?: string }
  | { kind: 'status'; onOpen: () => void; on: boolean; statusLabels?: [string, string] }
  | { kind: 'switch'; checked: boolean; onChange: (v: boolean) => void }
)

export function SettingsRow(p: SettingsRowProps) {
  const { t } = useTranslation('aiTrainUi')
  const left = (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {p.icon && <span className="shrink-0 text-muted-foreground">{p.icon}</span>}
      <span className="truncate text-[15px]">{p.title}</span>
      {p.info && <InfoTip text={p.info} />}
      {p.tag === 'beta' && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">Beta</span>}
      {p.tag === 'soon' && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{t('settingsRow.soon')}</span>}
    </div>
  )
  const base = 'flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left transition-colors'
  if (p.kind === 'switch') {
    return (
      <div className={cn(base, 'hover:bg-muted/40')}>
        {left}
        <Switch checked={p.checked} onCheckedChange={p.onChange} disabled={p.disabled} aria-label={typeof p.title === 'string' ? p.title : undefined} />
      </div>
    )
  }
  return (
    <button type="button" className={cn(base, 'hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60')} onClick={p.onOpen} disabled={p.disabled}>
      {left}
      <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
        {p.kind === 'dialog'
          ? <span>{p.label ?? t('settingsRow.setting')}{p.count != null ? ` (${p.count})` : ''}</span>
          : <StatusBadge on={p.on} labels={p.statusLabels} />}
        <ChevronRight className="h-4 w-4 opacity-60" />
      </span>
    </button>
  )
}

/**
 * Bọc danh sách dòng cài đặt. `advanced` = các dòng nâng cao, mặc định gập sau dòng
 * "Hiển thị thêm cài đặt ⌄" (trạng thái mở nhớ theo `storageKey`).
 */
export function SettingsList({ children, advanced, storageKey, className }: {
  children: ReactNode
  advanced?: ReactNode
  storageKey?: string
  className?: string
}) {
  const { t } = useTranslation('aiTrainUi')
  const [more, setMore] = useState<boolean>(() => {
    if (!storageKey) return false
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })
  const toggle = () => setMore((m) => {
    if (storageKey) { try { localStorage.setItem(storageKey, m ? '0' : '1') } catch { /* bỏ qua */ } }
    return !m
  })
  return (
    <div className={cn('divide-y rounded-lg border', className)}>
      {children}
      {advanced && more && advanced}
      {advanced && (
        <button type="button" onClick={toggle} className="flex w-full items-center justify-center gap-1 py-2.5 text-sm text-primary hover:bg-muted/40">
          {more ? <>{t('settingsRow.collapse')} <ChevronUp className="h-4 w-4" /></> : <>{t('settingsRow.showMore')} <ChevronDown className="h-4 w-4" /></>}
        </button>
      )}
    </div>
  )
}
